import { Grid, Typography } from '@material-ui/core';
import React from 'react';
import { formatDate } from '../sc-utilities';
import config from '../../config/config';
import { download, externalDownload } from '../sc-utilities/download.utils';
import { snackActions } from '../sc-utilities/SnackBar';
import axios from 'axios';
import Button from '@material-ui/core/Button';
import { NavLink } from 'react-router-dom';
import GetAppIcon from '@material-ui/icons/GetApp';

export const subComponents = (translations) => [
    { name: 'Command', path: '/command', permissions: null },
    { name: 'Creator', path: '/creator', permissions: null },
    { name: 'Mobile', path: '/mobile', permissions: null },
    { name: 'Motion', path: '/motion', permissions: null },
    { name: 'Remote', path: '/remote', permissions: null },
    { name: 'Work', path: '/work', permissions: null },
    { name: 'Work Verifier', path: '/workVerifier', permissions: null },
];

const BinaryImageTypeKeys = [
    'command',
    'mobile_win',
    'mobile_linux',
    'mobile_linux_app',
    'mobile_osx',
    'remote_win',
    'remote_win_msi',
    'remote_win_intune',
    'remote_osx',
    'work_osx_app',
    'work_osx_intel_legacy',
    'work_osx_intel',
    'work_osx_m1',
    'work_pc',
    'work_win_app',
    'work_chromebook_arm',
    'usb_tool_win',
    'usb_tool_osx',
    'work_verifier_pc',
    'work_verifier_osx_app',
    'work_verifier_osx_intel',
    'work_verifier_osx_intel_legacy',
];

export const DownloadButton = (
    button,
    availableKeys,
    classes,
    privateDownload,
    privateAvailableKeys,
    activeCustomerID,
    translations
) => (
    <Grid item xl={12} key={button.downloadKey}>
        <Button
            color={'primary'}
            variant={'contained'}
            className={classes.osDiv}
            onClick={() =>
                handleDownload(
                    privateAvailableKeys,
                    availableKeys,
                    activeCustomerID,
                    translations,
                    button.downloadKey,
                    privateDownload
                )
            }
        >
            <GetAppIcon />
            {button.name}{' '}
            <span className={classes.version}>
                {availableKeys[button.downloadKey].Version}{' '}
                {privateDownload ? `- ${availableKeys[button.downloadKey].CustomerName}` : ''}
            </span>
        </Button>
        <Typography className={classes.createdAt}>{`(${formatDate(
            availableKeys[button.downloadKey]?.CreatedAt
        )})`}</Typography>
    </Grid>
);

export const getDownloadButtons = (
    downloadButtons,
    availableKeys,
    classes,
    privateAvailableKeys,
    activeCustomerID,
    translations
) => {
    const publicButtons = availableKeys
        ? downloadButtons?.map(
              (button) =>
                  availableKeys[button.downloadKey] &&
                  DownloadButton(
                      button,
                      availableKeys,
                      classes,
                      false,
                      privateAvailableKeys,
                      activeCustomerID,
                      translations
                  )
          )
        : null;
    const privateButtons = privateAvailableKeys
        ? downloadButtons?.map(
              (button) =>
                  privateAvailableKeys[button.downloadKey] &&
                  DownloadButton(
                      button,
                      privateAvailableKeys,
                      classes,
                      true,
                      privateAvailableKeys,
                      activeCustomerID,
                      translations
                  )
          )
        : null;
    return (
        <span className={classes.button}>
            {publicButtons} {privateButtons}
        </span>
    );
};

export const checkIfAvailable = (
    availableKeys,
    downloadButtons,
    alternativeDownloads,
    classes,
    translations,
    privateAvailableKeys
) => {
    const hasAlternativeDownloads = availableKeys
        ? downloadButtons.some((button) => availableKeys[button.downloadKey])
        : false;
    const hasPrivateAlternativeDownloads = privateAvailableKeys
        ? downloadButtons.some((button) => privateAvailableKeys[button.downloadKey])
        : false;

    const types = availableKeys
        ? downloadButtons
              .filter((button) => availableKeys[button.downloadKey])
              .map((button) => availableKeys[button.downloadKey]?.BinaryImageTypeID)
        : [];
    const privateTypes = privateAvailableKeys
        ? downloadButtons
              .filter((button) => privateAvailableKeys[button.downloadKey])
              .map((button) => privateAvailableKeys[button.downloadKey]?.BinaryImageTypeID)
        : [];

    const typesForUrl = [...types, ...privateTypes];

    return (
        <Grid container direction="row" justify="center" alignItems="center">
            <Typography>
                {(hasAlternativeDownloads || hasPrivateAlternativeDownloads) && (
                    <NavLink
                        to={typesForUrl ? alternativeDownloads(typesForUrl) : `/downloads/binary-images`}
                        className={classes.content}
                    >
                        {translations['alternative_downloads'] || 'Alternative Downloads'}
                    </NavLink>
                )}
            </Typography>
        </Grid>
    );
};

export const handleDownload = async (
    privateAvailableKeys,
    availableKeys,
    activeCustomerID,
    translations,
    binaryImageTypeKey,
    privateDownload = false
) => {
    let objectURL = ``;
    let exportDataSuccessful = null;
    const available = privateDownload ? privateAvailableKeys : availableKeys;
    switch (available[binaryImageTypeKey]?.Type) {
        case 'Internal': {
            objectURL = `${config.backend_domain}:${config.backend_port}/binary-images/download?BinaryImageID=${available[binaryImageTypeKey].BinaryImageID}&ActiveCustomerID=${activeCustomerID}`;
            exportDataSuccessful = await download(objectURL);
            break;
        }
        case 'External': {
            exportDataSuccessful = externalDownload(available[binaryImageTypeKey]?.URL);
            break;
        }
        case 'External-AzureBlob': {
            objectURL = `${config.backend_domain}:${config.backend_port}/azure-blobs/download?Name=${
                available[binaryImageTypeKey]?.URL?.split('download/')?.[1]
            }`;
            exportDataSuccessful = await download(objectURL);
            break;
        }
        default:
            break;
    }
    if (!exportDataSuccessful) {
        return snackActions.snackBarComponent(
            translations['download_error_msg_software'] || `Could not download software `,
            'error'
        );
    }
};

export const checkAvailableKeys = async (activeCustomerID, Public = true) => {
    const requestObj = {
        BinaryImageTypeKeys,
        ActiveCustomerID: activeCustomerID,
        Token: localStorage.getItem('SESSION'),
        Public,
    };
    return await axios.post(
        `${config.backend_domain}:${config.backend_port}/check-available-binary-images`,
        requestObj
    );
};

export const alternativeDownloads = (types) => {
    return `/downloads/binary-images?BinaryImageTypes=${types}`;
};

export const styles = (theme) => ({
    root: {
        height: '100%',
        margin: theme.spacing(1.5),
        padding: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%',
        },
        overflow: 'scroll',
    },
    loadingScreen: {
        backgroundColor: 'transparent',
        boxShadow: 'none',
    },
    content: {
        padding: theme.spacing(2),
        textAlign: 'center',
        fontSize: '14px',
        color: '#666666',
        textDecoration: 'none',
    },
    osDiv: {
        padding: '10px 30px',
        margin: '5px',
        textTransform: 'none',
    },
    customToolbar: {
        marginLeft: 14,
        width: '900px',
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px',
    },
    grid: {
        padding: theme.spacing(2),
    },
    row: {
        padding: '8px 25px',
    },
    divider: {
        width: 'auto',
        height: '1px',
    },
    latestVersionSpan: {
        fontWeight: 'normal',
        fontSize: '16px',
    },
    buttonLink: {
        textDecoration: 'none',
        color: 'white',
    },
    link: {
        textDecoration: 'none',
        fontSize: '14px',
        color: '#666666',
    },
    storeButton: {
        margin: '0 5px',
    },
    version: {
        fontSize: '13px',
        marginLeft: '5px',
    },
    button: {
        textAlign: 'center',
    },
    createdAt: {
        textAlign: 'center',
        fontSize: '14px',
        color: '#666666',
    },
});
